import { useEffect, useState } from 'react';
import { createResource,getResources,deleteResource, updateResource} from '../../../router/api';
import toast from 'react-hot-toast';

interface InvoiceFormProps {
  blId: number;
  customerId: number;
  onClose: () => void;
  onSuccess?: (invoice: any) => void;
}

interface BL {
  id: number;
  numero: string;
  total_ht: number;
  total_tva: number;
  total_ttc: number;
  lignes: {
    designation: string;
    quantite: number;
    prix_unitaire_ht: number;
    total_ht: number;
  }[];
}

interface Client {
  id: number;
  raison_sociale: string;
  email: string;
  delai_paiement?: string;
}

export default function InvoiceForm({
  blId,
  customerId,
  onClose,
  onSuccess,
}: InvoiceFormProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [bl, setBl] = useState<BL | null>(null);
  const [client, setClient] = useState<Client | null>(null);

  const [dateFacture, setDateFacture] = useState(new Date().toISOString().split('T')[0]);
  const [dateEcheance, setDateEcheance] = useState('');
  const [notes, setNotes] = useState('');
  const [estCredit, setEstCredit] = useState(false);

  /* ================= CHARGEMENT BL + CLIENT ================= */
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [blRes, clientRes] = await Promise.all([
          getResources(`bons-livraison/${blId}`),
          getResources(`clients/${customerId}`),
        ]);

        const bonLivraison = blRes.data.data || blRes.data;
        const clientData = clientRes.data.data || clientRes.data;

        setBl(bonLivraison);
        setClient(clientData);

        // Calcul automatique de l'échéance
        calculateDueDate(dateFacture, clientData.delai_paiement || '30 jours');
      } catch (err) {
        toast.error('Erreur lors du chargement des données');
        onClose();
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [blId, customerId]);

  /* ================= CALCUL DATE ÉCHÉANCE ================= */
  const calculateDueDate = (dateStr: string, terms: string) => {
    const date = new Date(dateStr);
    let days = 30;

    if (terms.toLowerCase().includes('comptant')) days = 0;
    else if (terms.includes('7')) days = 7;
    else if (terms.includes('15')) days = 15;
    else if (terms.includes('30')) days = 30;
    else if (terms.includes('45')) days = 45;
    else if (terms.includes('60')) days = 60;

    date.setDate(date.getDate() + days);
    setDateEcheance(date.toISOString().split('T')[0]);
  };

  /* ================= FORMATAGE MONTANT ================= */
  const formatPrice = (amount: number | string) => {
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(Number(amount)) + ' GNF';
  };

  /* ================= SOUMISSION ================= */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!bl) return;

    setSaving(true);

    try {
      const payload = {
        bl_id: bl.id,
        date_facture: dateFacture,
        date_echeance: dateEcheance,
        total_ht: bl.total_ht,
        total_tva: bl.total_tva,
        total_ttc: bl.total_ttc,
        statut_paiement: 'IMPAYEE',
        montant_paye: 0,
        est_credit: estCredit,
        notes: notes || null,
      };

      const res = await createResource(`factures-client`, payload);

      toast.success(`Facture créée avec succès : ${res.data.data.numero}`);
      onSuccess?.(res.data.data);
      onClose();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erreur lors de la création de la facture');
    } finally {
      setSaving(false);
    }
  };

  /* ================= ÉCRAN DE CHARGEMENT ================= */
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-xl p-8 flex items-center gap-4">
          <i className="ri-loader-4-line animate-spin text-3xl text-blue-600"></i>
          <span className="text-lg font-medium">Chargement du bon de livraison...</span>
        </div>
      </div>
    );
  }

  if (!bl || !client) {
    return null;
  }

  /* ================= FORMULAIRE ================= */
  return (
    <div className="fixed inset-0 bg-black/40 z-50 overflow-y-auto">
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl border border-gray-200 max-w-5xl w-full max-h-[95vh] overflow-y-auto">

          {/* ================= HEADER ================= */}
          <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={onClose}
                  className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center transition"
                  title="Fermer"
                >
                  <i className="ri-arrow-left-line text-xl"></i>
                </button>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    Créer la facture
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    À partir du bon de livraison <strong>{bl.numero}</strong>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* ================= CONTENU ================= */}
          <form onSubmit={handleSubmit} className="p-6 space-y-8">

            {/* ================= CLIENT ================= */}
            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <i className="ri-user-3-line text-teal-600"></i>
                Client
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <input
                  readOnly
                  value={client.raison_sociale}
                  className="px-4 py-3 border border-gray-300 rounded-lg bg-gray-50"
                />
                <input
                  readOnly
                  value={client.email}
                  className="px-4 py-3 border border-gray-300 rounded-lg bg-gray-50"
                />
                <input
                  readOnly
                  value={client.delai_paiement || '30 jours'}
                  className="px-4 py-3 border border-gray-300 rounded-lg bg-gray-50"
                />
              </div>
            </div>

            {/* ================= DATES ================= */}
            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <i className="ri-calendar-line text-teal-600"></i>
                Dates de facturation
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date de facture *
                  </label>
                  <input
                    type="date"
                    value={dateFacture}
                    onChange={(e) => {
                      setDateFacture(e.target.value);
                      calculateDueDate(e.target.value, client.delai_paiement || '30 jours');
                    }}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date d'échéance
                  </label>
                  <input
                    type="date"
                    value={dateEcheance}
                    readOnly
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50"
                  />
                </div>
              </div>
            </div>

            {/* ================= ARTICLES ================= */}
            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <i className="ri-list-check-2 text-teal-600"></i>
                Articles à facturer
              </h3>
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Désignation</th>
                      <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase">Quantité</th>
                      <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase">P.U. HT</th>
                      <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase">Total HT</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {bl.lignes.map((ligne, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium">{ligne.designation}</td>
                        <td className="px-6 py-4 text-center text-sm">{ligne.quantite}</td>
                        <td className="px-6 py-4 text-right text-sm">{formatPrice(ligne.prix_unitaire_ht)}</td>
                        <td className="px-6 py-4 text-right text-sm font-semibold">{formatPrice(ligne.total_ht)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ================= TOTAUX ================= */}
            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <i className="ri-calculator-line text-teal-600"></i>
                Montants
              </h3>
              <div className="flex justify-end">
                <div className="w-full max-w-md bg-gray-50 rounded-xl p-6 space-y-3">
                  <div className="flex justify-between text-lg">
                    <span>Total HT</span>
                    <span className="font-semibold">{formatPrice(bl.total_ht)}</span>
                  </div>
                  <div className="flex justify-between text-lg">
                    <span>TVA (19,25%)</span>
                    <span className="font-semibold">{formatPrice(bl.total_tva)}</span>
                  </div>
                  <div className="flex justify-between text-2xl font-bold text-blue-600 border-t-2 border-blue-200 pt-4">
                    <span>Total TTC</span>
                    <span>{formatPrice(bl.total_ttc)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* ================= NOTES ================= */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes (facultatif)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                placeholder="Conditions particulières, mentions légales..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* ================= ACTIONS ================= */}
            <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium transition"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition flex items-center gap-2 disabled:opacity-50"
              >
                {saving && <i className="ri-loader-4-line animate-spin"></i>}
                <i className="ri-file-add-line"></i>
                Créer la facture
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}